import {
  CellValue,
  ListColumn,
  ListColumnId,
  ListData,
  ListId,
  ListMetadata,
  ListValue,
  ListWithoutValues,
  ListWithValues,
  NestedColumnValue,
  RowNumber,
} from './listDataManagerTypes'

const LIST_METADATA = new Map<ListId, ListMetadata>()
const LIST_DATA = new Map<ListId, ListData>()
const LISTS = new Map<ListId, ListWithoutValues>()
const CACHED_ROWS = new Map<ListId, Record<ListColumnId, Record<CellValue, RowNumber>>>()
const LIST_NESTED_VALUES = new Map<ListId, NestedColumnValue[]>()
const LIST_INDEXED_BY_COLUMN_VALUES = new Map<
  ListId,
  Record<ListColumnId, Record<CellValue, Record<ListColumnId, CellValue>>>
>()

export function registerList(listWithValues: ListWithValues) {
  const { id, list_values: _, ...listWithoutValues } = listWithValues

  const listMetadata = generateListMetadata(listWithoutValues)
  const listData = generateListData(listWithValues, listMetadata)

  // store a list reference without the values
  LISTS.set(id, listWithoutValues)

  // store the denormalized list data for performance
  LIST_DATA.set(id, listData)

  // store the denormalized list metadata for performance
  LIST_METADATA.set(id, listMetadata)
}

export function getListMetadata(listId: number | string): ListMetadata {
  return (
    LIST_METADATA.get(+listId) ?? {
      columnsById: {},
      columnsByPrecedence: [],
    }
  )
}

export function getListData(listId: number | string): ListData {
  return LIST_DATA.get(+listId) ?? ({} as ListData)
}

export function deregisterList(listId: number | string) {
  LISTS.delete(+listId)
  LIST_DATA.delete(+listId)
  LIST_METADATA.delete(+listId)
  CACHED_ROWS.delete(+listId)
  LIST_NESTED_VALUES.delete(+listId)
  LIST_INDEXED_BY_COLUMN_VALUES.delete(+listId)
}

export function deregisterAll() {
  ;[
    LISTS,
    LIST_DATA,
    LIST_METADATA,
    CACHED_ROWS,
    LIST_NESTED_VALUES,
    LIST_INDEXED_BY_COLUMN_VALUES,
  ].forEach(cache => {
    cache.clear()
  })
}

export function getListNestedValues(listId: number | string): NestedColumnValue[] {
  const id = Number(listId)

  const nestedValues = LIST_NESTED_VALUES.get(id)

  if (nestedValues) {
    return nestedValues
  }

  const rowsWithColumnIds = LIST_DATA.get(id)?.rowsWithColumnIds ?? {}
  const generated = generateNestedValues(Object.values(rowsWithColumnIds))

  LIST_NESTED_VALUES.set(id, generated)

  return generated
}

export function getUniqueValuesForListColumn(
  listId: number | string,
  listColumnId: number | string,
): string[] {
  const listData = getListData(listId)
  const uniqueValuesPerColumn = listData.uniqueValuesPerColumn
  const uniqueColumnValues = uniqueValuesPerColumn[Number(listColumnId)] ?? []
  return uniqueColumnValues
}

/**
 * Ideally this wouldn't exist... ideally we could memoize based on the inputs
 * to perform the equivalent of useEffect or something.
 *
 * This serves as a functional, low touch solution that still allows pre-calculation
 * of the uniqueValuesPerColumn property.
 *
 * @param colListId
 * @param colListColumnId
 * @param filter
 */
export function overrideListValuesPerColumn(
  listId: number | string,
  listColumnId: number | string,
  overrideValues: ListValue[],
) {
  const listData = getListData(listId)
  listData.listValuesPerColumn[listColumnId] = overrideValues

  // regenerate the unique values since they are dependent on listValuesPerColumn
  listData.uniqueValuesPerColumn = generateUniqueValuesPerColumn(listData.listValuesPerColumn)
}

export function getRowNumbersByColumnValue(
  listId: string | number,
  listColumnId: string | number,
): Record<CellValue, RowNumber> {
  const id = Number(listId)
  const columnId = Number(listColumnId)

  const cached = CACHED_ROWS.get(id) ?? []

  if (!cached[columnId]) {
    const listRows = LIST_DATA.get(id)?.rowsWithColumnIds ?? {}

    cached[columnId] = Object.keys(listRows).reduce((memo, rowNum) => {
      const row = listRows[Number(rowNum)]

      if (row) {
        const rowValue = row[Number(listColumnId)]

        if (rowValue && !memo[rowValue]) {
          memo[rowValue] = parseInt(rowNum, 10)
        }
      }

      return memo
    }, {})
  }

  CACHED_ROWS.set(id, cached)

  return cached[columnId]
}

/**
 * Indexes list rows by the values at provided listColumnId. This is very
 * helpful for things like dependent lists, where we need to find a row by
 * a given dependent key. In those cases, a single key value should always
 * point to a single specific row.
 *
 * This allows us to do fast lookups n(1) for other row values based on the key,
 * like this:
 * ```ts
 * const row = getRowsIndexedByColumnValue(1,2)["myKeyValue"]
 * const valueForRow = row[listColumnId]
 * ```
 *
 * @param listId id of the list
 * @param listColumnId id of the list by which to index the values
 */
export function getRowsIndexedByColumnValue(
  listId: ListId,
  listColumnId: ListColumnId,
): Record<CellValue, Record<ListColumnId, string>> {
  const cached = LIST_INDEXED_BY_COLUMN_VALUES.get(listId) ?? {}

  // if (!cached) {
  //   cached = {}
  //   LIST_INDEXED_BY_COLUMN_VALUES[listId] = cached
  // }

  if (!cached[listColumnId]) {
    const listRows = LIST_DATA.get(listId)?.rowsWithColumnIds ?? {}

    cached[listColumnId] = Object.values(listRows).reduce<
      Record<CellValue, Record<ListColumnId, CellValue>>
    >((memo, row) => {
      const rowValue = row[listColumnId]
      if (rowValue && !memo[rowValue]) {
        memo[rowValue] = row
      }
      return memo
    }, {})
  }

  LIST_INDEXED_BY_COLUMN_VALUES.set(listId, cached)

  return cached[listColumnId]
}

function generateListData(listWithValues: ListWithValues, listMetadata: ListMetadata): ListData {
  const { columnsById } = listMetadata
  // normalize into rows
  const rows = generateRows(listWithValues.list_values, columnsById)
  const rowsWithColumnIds = generateRowsWithColumnIds(listWithValues.list_values)

  // partition by columnId
  const listValuesPerColumn = generateListValuesPerColumn(listWithValues.list_values)

  // generate unique values per column
  const uniqueValuesPerColumn = generateUniqueValuesPerColumn(listValuesPerColumn)

  return {
    listValues: listWithValues.list_values,
    rows,
    rowsWithColumnIds,
    listValuesPerColumn,
    uniqueValuesPerColumn,
  }
}

function generateRows(
  listValues: ListValue[],
  columnsById: Record<ListColumnId, ListColumn>,
): string[][] {
  return listValues.reduce<string[][]>((memo, item) => {
    if (!item.value) {
      return memo
    }

    const values = memo[item.row_number] ?? []
    const column = columnsById[item.list_column_id]

    if (column) values[column.precedence] = item.value

    memo[item.row_number] = values

    return memo
  }, [])
}

/**
 * This is a slightly different variate of the "rows" generated for lists.
 * Turns out we had two places in code that did that logic and they both did
 * it a little different.  For now, we keep both ways to not make the
 * number of changes even greater.
 *
 */
function generateRowsWithColumnIds(
  listValues: ListValue[],
): Record<RowNumber, Record<ListColumnId, string>> {
  const listValueRowMap = listValues.reduce<Record<RowNumber, Record<ListColumnId, string>>>(
    (memo, listValue) => {
      memo[listValue.row_number] = {
        ...(memo[listValue.row_number] ?? {}),
        [listValue.list_column_id]: listValue.value,
      }

      return memo
    },
    {},
  )

  return listValueRowMap
}

function generateNestedValues(listRows: Record<ListColumnId, string>[]): NestedColumnValue[] {
  const nestedValues = {} as Record<string, NestedColumnValue>
  const forParent: Record<string, Record<string, NestedColumnValue>> = {}

  // go through every column
  for (const listRow of listRows) {
    const rowListColumnIds = Object.keys(listRow)
    const row = Object.values(listRow)

    for (let i = row.length - 1; i >= 0; i--) {
      const value = row[i]

      if (typeof value === 'undefined') continue

      // last value in the list.
      // should always be unique
      if (i === row.length - 1) {
        if (!value) continue // Skip blank values

        const newObj = {
          columnId: rowListColumnIds[i] ?? '',
          value: value,
          label: value,
          children: [],
          row,
          disabled: false,
        }
        // this key should always be unique. So we can be assured that pushing it straight is fine.
        const key = row.slice(0, Math.max(i, 1)).join('.')

        forParent[key] ??= {}

        if (row.length === 1) {
          nestedValues[value] = newObj
        } else {
          forParent[key][value] = newObj
        }

        continue
      }

      if (i === 0) {
        // top level
        nestedValues[value] = {
          columnId: rowListColumnIds[i] ?? '',
          value: value,
          label: value,
          row,
          children: forParent[value] ? Object.values(forParent[value]) : [],
          disabled: false,
        }

        continue
      }

      // middle case
      if (value === '') continue // Skip blank values

      const key = row.slice(0, Math.max(i + 1, 1)).join('.')
      const midObj = {
        columnId: rowListColumnIds[i] ?? '',
        value: value,
        label: value,
        row,
        disabled: false,
        children: Object.values(forParent[key] ?? {}),
      }
      const newKey = row.slice(0, Math.max(i, 1)).join('.')

      forParent[newKey] ??= {}
      forParent[newKey][value] = midObj
    }
  }
  return Object.values(nestedValues)
}

function generateUniqueValuesPerColumn(listValuesPerColumn: Record<ListColumnId, ListValue[]>) {
  // map the sets to sorted arrays
  const sortedUniqueValuesPerColumn = Object.keys(listValuesPerColumn).reduce(
    (memo, listColumnId) => {
      const columnListValues = listValuesPerColumn[Number(listColumnId)] ?? []
      const values = columnListValues.map(listValue => listValue.value).filter(val => val !== '')

      // extract unique values in the order they appear in the list
      const reduced = values.reduce<{ ordered: string[]; seen: Record<string, boolean> }>(
        (memo, value) => {
          if (!memo.seen[value]) {
            memo.seen[value] = true
            memo.ordered.push(value)
          }
          return memo
        },
        { ordered: [], seen: {} },
      )

      memo[Number(listColumnId)] = reduced.ordered
      return memo
    },
    {},
  )

  return sortedUniqueValuesPerColumn
}

function generateListValuesPerColumn(
  listValues: ListValue[] = [],
): Record<ListColumnId, ListValue[]> {
  const listValuesPerColumn = listValues.reduce<Record<ListColumnId, ListValue[]>>(
    (memo, listValue) => {
      const values = memo[listValue.list_column_id] ?? []

      values.push(listValue)

      memo[listValue.list_column_id] = values

      return memo
    },
    {},
  )

  return listValuesPerColumn
}

function orderByListColumnPrecedenceAsc(a: { precedence?: number }, b: { precedence?: number }) {
  const aPrecedence = a.precedence ?? 0
  const bPrecedence = b.precedence ?? 0
  return aPrecedence - bPrecedence
}

function generateListMetadata(listWithoutValues: ListWithoutValues): ListMetadata {
  const listColumns = listWithoutValues.list_columns
  const columnsByPrecedence = [...listColumns]
  columnsByPrecedence.sort(orderByListColumnPrecedenceAsc)

  const columnsById = listColumns.reduce<Record<number, ListColumn>>((memo, item) => {
    memo[item.id] = item
    return memo
  }, {})

  return {
    columnsById,
    columnsByPrecedence,
  }
}
